import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Share2, Copy, Clock, Check, X, Loader2 } from "lucide-react";
import ModalShell from "@/components/ModalShell";

/**
 * Admin-only share dialog for a single asset.
 * Creates a 24-hour public link; recipient hits /s/:token and can download without
 * an account. Fixed 24h TTL per product decision.
 */
export default function ShareAssetModal({ open, asset, onClose }) {
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);

  // Reset local state whenever the modal is re-opened for a fresh asset.
  useEffect(() => {
    if (open) {
      setLink(null);
      setCopied(false);
    }
  }, [open, asset?.id]);

  async function generate() {
    if (!asset) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/share-links", {
        asset_id: asset.id,
        expires_in_hours: 24,
      });
      const publicUrl = data.url && data.url.startsWith("http")
        ? data.url
        : `${window.location.origin}/s/${data.token}`;
      setLink({ ...data, publicUrl });
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setLoading(false);
  }

  async function copy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.publicUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  }

  return (
    <ModalShell open={open} onClose={onClose} maxWidth="max-w-md" testId="share-asset-modal">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-1 hover:bg-surface-elev rounded-sm"
        data-testid="share-close"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2 flex items-center gap-2">
        <Share2 className="h-3 w-3" /> Public Share
      </div>
      <h2 className="font-display text-2xl uppercase font-bold tracking-tight">
        {asset?.title || "Asset"}
      </h2>
      <p className="text-sm text-muted-foreground mt-3">
        Create a link anyone can use to download this asset — no account needed.
        The link stops working automatically after 24 hours.
      </p>

      {!link ? (
        <button
          onClick={generate}
          disabled={loading}
          className="mt-6 w-full bg-foreground text-background py-3 uppercase text-xs font-semibold tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="share-generate-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
          {loading ? "Creating link…" : "Generate 24-hour link"}
        </button>
      ) : (
        <div className="mt-6 space-y-3">
          <div className="border border-border rounded-sm p-3 bg-surface-elev flex items-center gap-2">
            <input
              readOnly
              value={link.publicUrl}
              className="flex-1 bg-transparent text-xs font-mono outline-none"
              onFocus={(e) => e.target.select()}
              data-testid="share-link-input"
            />
            <button
              onClick={copy}
              className="p-2 rounded-sm bg-foreground text-background hover:opacity-90 transition"
              data-testid="share-copy-btn"
              aria-label="Copy link"
              title="Copy link"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground flex items-center gap-2">
            <Clock className="h-3 w-3" />
            Expires {new Date(link.expires_at).toLocaleString()}
          </div>
        </div>
      )}
    </ModalShell>
  );
}
